// frontend/src/pages/VoranmeldungPage.jsx — Club Pre-Registration (multi-athlete)
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

// ── Division filter ─────────────────────────────────────────────────────────
// Eine Regel für alle Masken: lib/divisionsFilter.js (passendeDivisionen).
// Hier stand bis 27.09.2026 eine eigene Abschrift mit anderen Regeln (Kampf:
// bis 8 Jahre über der Klasse, Alter zum heutigen Tag). Seit der Server
// streng prüft, hätte sie Kategorien angeboten, die er ablehnt.
import { passendeDivisionen } from '../lib/divisionsFilter';

const filterForAthlete = (allDiv, profil, stichtag) => passendeDivisionen(allDiv, profil, stichtag);

// ── Empty athlete template ───────────────────────────────────────────────────

let nextId = 1;
const emptyAthlete = () => ({
  _id: nextId++,
  vorname: '',
  nachname: '',
  geburtsdatum: '',
  geschlecht: '',
  gewicht: '',
  selectedCodes: [],
  result: null, // null | 'ok' | 'error'
  resultMsg: '',
});

// ── Styles (inline, no CSS file needed) ─────────────────────────────────────

const S = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
    color: '#e0e0e0',
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: '2rem 1rem',
  },
  wrap: { maxWidth: 900, margin: '0 auto' },
  logo: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' },
  logoText: { fontSize: '1.4rem', fontWeight: 800, color: '#fff', letterSpacing: '0.04em' },
  badge: {
    background: 'rgba(220,38,38,0.15)',
    border: '1px solid rgba(220,38,38,0.4)',
    borderRadius: 8,
    padding: '1rem 1.5rem',
    marginBottom: '1.5rem',
  },
  badgeName: { fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: 0 },
  badgeMeta: { fontSize: '0.88rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.25rem' },
  section: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '1.25rem 1.5rem',
    marginBottom: '1.5rem',
  },
  sectionTitle: { fontSize: '0.8rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' },
  input: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    color: '#fff',
    padding: '0.6rem 0.85rem',
    fontSize: '0.95rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    color: '#fff',
    padding: '0.6rem 0.85rem',
    fontSize: '0.95rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  label: { fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.3rem', display: 'block' },
  row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' },
  athleteCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: '1rem',
    marginBottom: '1rem',
    position: 'relative',
  },
  athleteCardOk: {
    border: '1px solid rgba(34,197,94,0.5)',
    background: 'rgba(34,197,94,0.05)',
  },
  athleteCardErr: {
    border: '1px solid rgba(239,68,68,0.5)',
    background: 'rgba(239,68,68,0.05)',
  },
  removeBtn: {
    position: 'absolute', top: '0.75rem', right: '0.75rem',
    background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 6, color: '#ef4444', cursor: 'pointer',
    padding: '0.2rem 0.5rem', fontSize: '0.8rem',
  },
  athleteNum: { fontWeight: 700, fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.6rem' },
  addBtn: {
    background: 'rgba(59,130,246,0.15)',
    border: '1px solid rgba(59,130,246,0.4)',
    borderRadius: 8, color: '#93c5fd',
    padding: '0.6rem 1.2rem', fontSize: '0.88rem',
    fontWeight: 600, cursor: 'pointer', marginBottom: '1.5rem',
  },
  submitBtn: {
    background: 'linear-gradient(135deg, #dc2626, #991b1b)',
    border: 'none', borderRadius: 8,
    color: '#fff', padding: '0.85rem 2rem',
    fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
    width: '100%',
  },
  submitBtnDisabled: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8, color: 'rgba(255,255,255,0.3)',
    padding: '0.85rem 2rem', fontSize: '1rem',
    fontWeight: 700, cursor: 'not-allowed', width: '100%',
  },
  tagGrid: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem' },
  tag: {
    padding: '0.25rem 0.6rem', borderRadius: 6,
    fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.7)',
    userSelect: 'none',
  },
  tagActive: {
    background: 'rgba(220,38,38,0.2)',
    border: '1px solid rgba(220,38,38,0.5)',
    color: '#fca5a5',
  },
  divGroup: { marginBottom: '0.75rem' },
  divGroupTitle: { fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' },
  success: { textAlign: 'center', padding: '3rem 1rem' },
  successIcon: { fontSize: '3rem', marginBottom: '1rem' },
  successTitle: { fontSize: '1.5rem', fontWeight: 700, color: '#22c55e', marginBottom: '0.5rem' },
  successSub: { fontSize: '0.95rem', color: 'rgba(255,255,255,0.6)' },
  resultOk: { color: '#22c55e', fontSize: '0.8rem', marginTop: '0.5rem' },
  resultErr: { color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' },
  errorBox: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '1rem', color: '#fca5a5', marginBottom: '1.5rem' },
};

// ── AthleteCard ──────────────────────────────────────────────────────────────

function AthleteCard({ athlete, index, allDivisionen, altersStichtag, onChange, onRemove, submitted }) {
  const filteredDivs = useCallback(() => {
    if (!allDivisionen.length) return [];
    const profil = {
      geburtsdatum: athlete.geburtsdatum || null,
      geschlecht: athlete.geschlecht || null,
      gewicht: athlete.gewicht || null,
    };
    return filterForAthlete(allDivisionen, profil, altersStichtag);
  }, [allDivisionen, altersStichtag, athlete.geburtsdatum, athlete.geschlecht, athlete.gewicht]);

  const groupedDivs = useCallback(() => {
    const groups = {};
    for (const div of filteredDivs()) {
      const t = div.Division_Type || 'Sonstige';
      if (!groups[t]) groups[t] = [];
      groups[t].push(div);
    }
    return groups;
  }, [filteredDivs]);

  const toggleCode = (code) => {
    const current = athlete.selectedCodes;
    if (current.includes(code)) {
      onChange({ selectedCodes: current.filter(c => c !== code) });
    } else {
      onChange({ selectedCodes: [...current, code] });
    }
  };

  const cardStyle = athlete.result === 'ok'
    ? { ...S.athleteCard, ...S.athleteCardOk }
    : athlete.result === 'error'
    ? { ...S.athleteCard, ...S.athleteCardErr }
    : S.athleteCard;

  const groups = groupedDivs();

  return (
    <div style={cardStyle}>
      {!submitted && (
        <button style={S.removeBtn} onClick={onRemove} type="button">✕</button>
      )}
      <div style={S.athleteNum}>Athlet {index + 1}</div>
      <div style={S.row}>
        <div>
          <label style={S.label}>Vorname *</label>
          <input style={S.input} value={athlete.vorname} placeholder="Vorname"
            onChange={e => onChange({ vorname: e.target.value })} disabled={submitted} />
        </div>
        <div>
          <label style={S.label}>Nachname *</label>
          <input style={S.input} value={athlete.nachname} placeholder="Nachname"
            onChange={e => onChange({ nachname: e.target.value })} disabled={submitted} />
        </div>
        <div>
          <label style={S.label}>Geburtsdatum *</label>
          <input style={S.input} type="date" value={athlete.geburtsdatum}
            onChange={e => onChange({ geburtsdatum: e.target.value })} disabled={submitted} />
        </div>
        <div>
          <label style={S.label}>Geschlecht *</label>
          <select style={S.select} value={athlete.geschlecht}
            onChange={e => onChange({ geschlecht: e.target.value })} disabled={submitted}>
            <option value="">— wählen —</option>
            <option value="männlich">Männlich</option>
            <option value="weiblich">Weiblich</option>
          </select>
        </div>
        <div>
          <label style={S.label}>Gewicht (kg) *</label>
          <input style={S.input} type="number" step="0.1" min="10" max="250"
            value={athlete.gewicht} placeholder="z.B. 70"
            onChange={e => onChange({ gewicht: e.target.value })} disabled={submitted} />
        </div>
      </div>

      {(athlete.vorname || athlete.nachname || athlete.geburtsdatum) && Object.keys(groups).length > 0 && (
        <div>
          <label style={S.label}>Kategorien</label>
          {Object.entries(groups).map(([type, divs]) => (
            <div key={type} style={S.divGroup}>
              <div style={S.divGroupTitle}>{type}</div>
              <div style={S.tagGrid}>
                {divs.map(div => (
                  <span
                    key={div.Division_Code}
                    style={athlete.selectedCodes.includes(div.Division_Code)
                      ? { ...S.tag, ...S.tagActive }
                      : S.tag}
                    onClick={() => !submitted && toggleCode(div.Division_Code)}
                  >
                    {div.Division_Name || div.Division_Code}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {athlete.selectedCodes.length === 0 && (
            <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', marginTop: '0.25rem' }}>
              Keine Kategorie gewählt — Athlet wird ohne Kategorie eingetragen.
            </div>
          )}
        </div>
      )}

      {athlete.result === 'ok' && (
        <div style={S.resultOk}>✓ {athlete.resultMsg || 'Angemeldet'}</div>
      )}
      {athlete.result === 'error' && (
        <div style={S.resultErr}>✗ {athlete.resultMsg || 'Fehler bei der Anmeldung'}</div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function VoranmeldungPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const vereinParam = searchParams.get('verein') || '';

  const [turnier, setTurnier] = useState(null);
  const [loadingTurnier, setLoadingTurnier] = useState(true);
  const [turnierError, setTurnierError] = useState(null);
  const [allDivisionen, setAllDivisionen] = useState([]);
  const [altersStichtag, setAltersStichtag] = useState(null);

  const [vereinName, setVereinName] = useState(vereinParam);
  const [athletes, setAthletes] = useState([emptyAthlete()]);
  const [submitting, setSubmitting] = useState(false);
  const [allSubmitted, setAllSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setTurnierError('Kein Anmeldelink angegeben.');
      setLoadingTurnier(false);
      return;
    }
    fetch(`/api/quickreg/form/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setTurnier(data.turnier);
        else setTurnierError(data.error || 'Link ungültig oder abgelaufen.');
      })
      .catch(() => setTurnierError('Verbindungsfehler — bitte Seite neu laden.'))
      .finally(() => setLoadingTurnier(false));
  }, [token]);

  // Divisionen über den Einladungs-Token laden — /api/divisionen selbst
  // verlangt inzwischen einen Login.
  useEffect(() => {
    if (!token) return;
    fetch(`/api/quickreg/divisionen/${token}`)
      .then(r => r.json())
      .then(data => { if (data.success && data.data) { setAllDivisionen(data.data); setAltersStichtag(data.altersStichtag || null); } })
      .catch(() => {});
  }, [token]);

  const updateAthlete = (id, changes) => {
    setAthletes(prev => prev.map(a => a._id === id ? { ...a, ...changes } : a));
  };

  const removeAthlete = (id) => {
    setAthletes(prev => prev.length > 1 ? prev.filter(a => a._id !== id) : prev);
  };

  const addAthlete = () => setAthletes(prev => [...prev, emptyAthlete()]);

  // Gewicht ist Pflicht seit 16.09.2026 (routes/quickreg.js) — ohne kam
  // der Eintrag erst als Fehler vom Server zurück.
  const vollstaendig = a =>
    a.vorname.trim() && a.nachname.trim() && a.geburtsdatum && a.geschlecht && parseFloat(a.gewicht) > 0;
  const isValid = athletes.some(vollstaendig);

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);

    const validAthletes = athletes.filter(vollstaendig);

    for (const athlete of validAthletes) {
      try {
        const res = await fetch('/api/quickreg/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token,
            vorname: athlete.vorname.trim(),
            nachname: athlete.nachname.trim(),
            geburtsdatum: athlete.geburtsdatum,
            geschlecht: athlete.geschlecht,
            gewicht: athlete.gewicht ? parseFloat(athlete.gewicht) : undefined,
            verein_name: vereinName.trim() || undefined,
            division_codes: athlete.selectedCodes,
          }),
        });
        const data = await res.json();
        updateAthlete(athlete._id, {
          result: data.success ? 'ok' : 'error',
          resultMsg: data.success
            ? `${athlete.vorname} ${athlete.nachname} erfolgreich angemeldet`
            : (data.error || 'Fehler'),
        });
      } catch {
        updateAthlete(athlete._id, { result: 'error', resultMsg: 'Netzwerkfehler' });
      }
    }

    setSubmitting(false);
    setAllSubmitted(true);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loadingTurnier) {
    return (
      <div style={S.page}>
        <div style={{ ...S.wrap, textAlign: 'center', paddingTop: '4rem', color: 'rgba(255,255,255,0.5)' }}>
          Lade Turnierdaten…
        </div>
      </div>
    );
  }

  if (turnierError) {
    return (
      <div style={S.page}>
        <div style={S.wrap}>
          <div style={S.errorBox}>{turnierError}</div>
        </div>
      </div>
    );
  }

  if (allSubmitted && athletes.every(a => a.result === 'ok')) {
    return (
      <div style={S.page}>
        <div style={S.wrap}>
          <div style={S.success}>
            <div style={S.successIcon}>✅</div>
            <div style={S.successTitle}>Voranmeldung eingereicht!</div>
            <div style={S.successSub}>
              {athletes.filter(a => a.result === 'ok').length} Athlet(en) wurden angemeldet und warten auf Bestätigung durch den Veranstalter.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        {/* Header */}
        <div style={S.logo}>
          <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="20" fill="#dc2626" fillOpacity="0.2" />
            <text x="20" y="27" textAnchor="middle" fontSize="18" fill="#dc2626" fontWeight="800">T</text>
          </svg>
          <span style={S.logoText}>TDA International</span>
        </div>

        {turnier && (
          <div style={S.badge}>
            <div style={S.badgeName}>{turnier.name}</div>
            <div style={S.badgeMeta}>
              {turnier.datum && new Date(turnier.datum).toLocaleDateString('de-DE')}
              {turnier.ort && ` · ${turnier.ort}`}
            </div>
          </div>
        )}

        <div style={S.section}>
          <div style={S.sectionTitle}>Vereinsname</div>
          <input
            style={S.input}
            placeholder="z.B. TDA München e.V."
            value={vereinName}
            onChange={e => setVereinName(e.target.value)}
            disabled={allSubmitted}
          />
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>Athleten ({athletes.length})</div>

          {athletes.map((a, i) => (
            <AthleteCard
              key={a._id}
              athlete={a}
              index={i}
              allDivisionen={allDivisionen}
              altersStichtag={altersStichtag}
              onChange={changes => updateAthlete(a._id, changes)}
              onRemove={() => removeAthlete(a._id)}
              submitted={allSubmitted}
            />
          ))}

          {!allSubmitted && (
            <button style={S.addBtn} onClick={addAthlete} type="button">
              + Weiteren Athleten hinzufügen
            </button>
          )}
        </div>

        {!allSubmitted && (
          <button
            style={isValid && !submitting ? S.submitBtn : S.submitBtnDisabled}
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting
              ? 'Wird gesendet…'
              : `${athletes.filter(vollstaendig).length} Athlet(en) anmelden`}
          </button>
        )}

        {allSubmitted && (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem' }}>
            Voranmeldung abgeschlossen — der Veranstalter wird die Einträge bestätigen.
          </div>
        )}
      </div>
    </div>
  );
}
